import React, { useEffect, useRef } from "react";
import type { Dealer } from ".././types/index";
import WorkshopItem from "./WorkshopItem";

interface WorkshopListProps {
  workshops: Dealer[];
  expandedId: string | null;
  selectedId: string | null;
  pinnedId?: string | null;
  onToggleExpand: (id: string) => void;
  refreshUI: number;
  clearSelection: () => void;
  totalCount?: number;
}

const WorkshopList: React.FC<WorkshopListProps> = ({
  workshops,
  expandedId,
  selectedId,
  pinnedId,
  onToggleExpand,
  refreshUI,
  clearSelection,
  totalCount,
}) => {
  // ref to the currently selected <li>
  const selectedRef = useRef<HTMLLIElement | null>(null);

  // auto-scroll + flash highlight on selection or distance refresh
  useEffect(() => {
    if (!selectedRef.current) return;
    selectedRef.current.scrollIntoView({ block: "nearest", behavior: "smooth" });

    // restart the highlight animation even if the same item is reselected
    selectedRef.current.classList.remove("flash-highlight");
    // force reflow
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    selectedRef.current.offsetWidth;
    selectedRef.current.classList.add("flash-highlight");
  }, [selectedId, refreshUI]);

  const pinnedWorkshop = pinnedId
    ? workshops.find((w) => w.dealerId === pinnedId)
    : null;
  const orderedWorkshops = pinnedWorkshop
    ? [
        pinnedWorkshop,
        ...workshops.filter((w) => w.dealerId !== pinnedWorkshop.dealerId),
      ]
    : workshops;

  return (
    <div
      style={{
        background: "#ffffff",
        border: "1px solid #d9d9d9",
        borderRadius: "8px",
        padding: "0.85rem 1rem 0.85rem 1rem",
        marginBottom: "0.5rem",
        boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
        fontFamily: "Roboto, Arial, sans-serif",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <h3
        style={{
          fontSize: "14px",
          margin: "0 0 0.35rem 0",
          color: "#002663",
          fontWeight: 700,
          letterSpacing: 0.1,
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        Nearby Workshops
        <span
          style={{
            fontSize: "12px",
            color: "#5b6d8a",
            fontWeight: 600,
          }}
        >
          {workshops.length} / {totalCount ?? workshops.length}
        </span>
      </h3>

      <ul
        style={{
          listStyle: "none",
          padding: 0,
          margin: 0,
          display: "flex",
          flexDirection: "column",
          gap: "0.35rem",
        }}
      >
        {orderedWorkshops.map((dealer) => {
          const isSelected = selectedId === dealer.dealerId;
          return (
            <WorkshopItem
              key={`${dealer.dealerId}-${refreshUI}`}
              dealer={dealer}
              isExpanded={expandedId === dealer.dealerId}
              isSelected={isSelected}
              isClosest={dealer.isClosest}
              onToggle={() => onToggleExpand(dealer.dealerId)}
              refreshUI={refreshUI}
              // NEW: wire highlight + scroll
              itemRef={isSelected ? selectedRef : undefined}
              className={
                isSelected ? "ws-item ws-item--selected" : "ws-item"
              }
            />
          );
        })}
      </ul>

      <button
        onClick={clearSelection}
        style={{
          marginTop: "0.5rem",
          fontSize: "12px",
          color: "#666",
          background: "transparent",
          border: "1px solid #ccc",
          padding: "4px 8px",
          borderRadius: "6px",
          cursor: "pointer",
        }}
      >
        Clear Selection
      </button>
    </div>
  );
};

export default WorkshopList;
